import { useState } from 'react';
import type { ReactNode } from 'react';
import { MemoryEntry } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';
import { AGENT_DEPARTMENTS } from '@/config/agentDepartments.config';
import { MemoryDomain } from '@/types/orchestrator';

export const MEMORY_DOMAINS: MemoryDomain[] = [
  'company', 'brand', 'services', 'sop', 'creators', 'clients', 'leads',
  'partnerships', 'contracts', 'projects', 'content', 'decisions', 'knowledge', 'agent_learnings',
];

export interface MemoryFormValues {
  title: string;
  domain: string;
  content: string;
  source: string;
  tags: string;
  visibility: 'team' | 'private';
  agentAccess: string[];
}

function toValues(m?: MemoryEntry | null): MemoryFormValues {
  return {
    title: m?.title ?? '',
    domain: m?.domain ?? MEMORY_DOMAINS[0],
    content: m?.content ?? '',
    source: m?.source ?? '',
    tags: m?.tags.join(', ') ?? '',
    visibility: m?.visibility ?? 'team',
    agentAccess: m?.agentAccess ?? [],
  };
}

export function MemoryFormDrawer({ entry, onClose, onSubmit }: { entry?: MemoryEntry | null; onClose: () => void; onSubmit: (v: MemoryFormValues) => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState<MemoryFormValues>(toValues(entry));
  const [error, setError] = useState('');

  function set<K extends keyof MemoryFormValues>(key: K, val: MemoryFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  function toggleAgent(dept: string) {
    setValues((v) => ({
      ...v,
      agentAccess: v.agentAccess.includes(dept) ? v.agentAccess.filter((d) => d !== dept) : [...v.agentAccess, dept],
    }));
  }

  return (
    <EntityDrawer title={entry ? t('memoryForm.editTitle') : t('memoryForm.newTitle')} onClose={onClose}>
      <Field label={t('memoryForm.title')} required><input className="text-input" value={values.title} onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label={t('memoryForm.domain')}>
        <select className="text-input" value={values.domain} onChange={(e) => set('domain', e.target.value)}>
          {MEMORY_DOMAINS.map((d) => <option key={d} value={d}>{t(`memoryDomain.${d}`)}</option>)}
        </select>
      </Field>
      <Field label={t('memoryForm.content')} required><textarea className="text-input" rows={4} value={values.content} onChange={(e) => set('content', e.target.value)} /></Field>
      <Field label={t('memoryForm.source')}><input className="text-input" value={values.source} onChange={(e) => set('source', e.target.value)} /></Field>
      <Field label={t('memoryForm.tags')}><input className="text-input" placeholder={t('memoryForm.tagsPlaceholder')} value={values.tags} onChange={(e) => set('tags', e.target.value)} /></Field>
      <Field label={t('memoryForm.visibility')}>
        <select className="text-input" value={values.visibility} onChange={(e) => set('visibility', e.target.value as 'team' | 'private')}>
          <option value="team">{t('memoryForm.visibilityTeam')}</option>
          <option value="private">{t('memoryForm.visibilityPrivate')}</option>
        </select>
      </Field>
      <Field label={t('memoryForm.agentAccess')}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {AGENT_DEPARTMENTS.map((d) => (
            <label key={d.department} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={values.agentAccess.includes(d.department)} onChange={() => toggleAgent(d.department)} />
              {t(d.nameKey)}
            </label>
          ))}
        </div>
      </Field>

      {error && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={() => (values.title.trim() && values.content.trim() ? onSubmit(values) : setError(t('memoryForm.required')))}>{t('common.save')}</button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
    </div>
  );
}
